import { useState } from "react";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarMonthIcon,
  EventAvailable as EventAvailableIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAuth } from "../../context/AuthContext";
import { useOrganization } from "../../context/OrganizationContext";
import { useThemeMode } from "../../context/ThemeContext";
import { authService } from "../../services/firebase/auth.service";

export default function EmployeeAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { organization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleTheme } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  // Check if employee can view all schedules
  const canViewAllSchedules = organization?.settings?.allowEmployeeViewAllSchedules ?? false;

  // Navigation items for reuse (dynamic based on settings)
  const navItems = [
    { label: "Dashboard", path: "/employee/dashboard", icon: <DashboardIcon /> },
    { label: "My Schedule", path: "/employee/schedule", icon: <CalendarMonthIcon /> },
    { label: "My Availability", path: "/employee/availability", icon: <EventAvailableIcon /> },
    ...(canViewAllSchedules
      ? [{ label: "Team Schedule", path: "/employee/team-schedule", icon: <GroupsIcon /> }]
      : []),
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth");
    } catch (error) {
      console.error("Logout error:", error);
    }
    handleClose();
  };

  const handleLeaveOrganization = async () => {
    if (!user?.uid) return;

    try {
      setIsLeaving(true);
      await authService.removeUserFromOrganization(user.uid);
      enqueueSnackbar("You have left the organization", { variant: "success" });
      navigate("/no-organization");
    } catch (error) {
      console.error("Leave organization error:", error);
      const message = error instanceof Error ? error.message : "Failed to leave organization";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsLeaving(false);
      setLeaveDialogOpen(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: "none",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: { xs: 1, md: 3 }, minHeight: { xs: 56, sm: 64 } }}>
        {/* Left Side: Logo and Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 3 } }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* App Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              cursor: "pointer",
            }}
            onClick={() => navigate("/employee/dashboard")}
          >
            <Box
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarViewMonthIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary.main"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              ShiftGrid
            </Typography>
          </Box>

          {/* Desktop Navigation Menu */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  startIcon={item.icon}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color:
                      location.pathname === item.path
                        ? "primary.main"
                        : "text.primary",
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>

        {/* Right Side Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
          {/* Dark/Light Mode Toggle - hide on very small screens */}
          <Tooltip title={mode === 'dark' ? "Light Mode" : "Dark Mode"}>
            <IconButton
              size="medium"
              onClick={toggleTheme}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                display: { xs: "none", sm: "flex" },
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Profile Avatar */}
          <Tooltip title="Account">
            <IconButton
              size="medium"
              onClick={handleMenu}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                p: 0.5,
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  bgcolor: "primary.main",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  fontWeight: 600,
                }}
              >
                {user?.displayName ? getInitials(user.displayName) : "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{
            mt: 1,
            "& .MuiPaper-root": {
              minWidth: 240,
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {organization?.name || "Organization"}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              handleClose();
              setLeaveDialogOpen(true);
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Leave Organization</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        {/* Leave Organization Confirmation Dialog */}
        <Dialog
          open={leaveDialogOpen}
          onClose={() => !isLeaving && setLeaveDialogOpen(false)}
        >
          <DialogTitle>Leave Organization?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to leave <strong>{organization?.name}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              You will no longer have access to your schedules and will need a new invitation to rejoin.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setLeaveDialogOpen(false)}
              disabled={isLeaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLeaveOrganization}
              color="error"
              variant="contained"
              disabled={isLeaving}
            >
              {isLeaving ? <CircularProgress size={20} /> : "Leave Organization"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mobile Navigation Drawer */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: 280,
              boxSizing: "border-box",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Drawer Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8 },
                }}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  navigate("/employee/dashboard");
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CalendarViewMonthIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  ShiftGrid
                </Typography>
              </Box>
              <IconButton onClick={() => setMobileDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* User Info */}
            <Box sx={{ mb: 2, px: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {organization?.name}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Navigation Items */}
            <List disablePadding>
              {navItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&.Mui-selected": {
                      bgcolor: "primary.light",
                      color: "primary.main",
                      "& .MuiListItemIcon-root": {
                        color: "primary.main",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Dark Mode Toggle in drawer */}
            <ListItemButton
              onClick={toggleTheme}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </ListItemIcon>
              <ListItemText primary={mode === 'dark' ? "Light Mode" : "Dark Mode"} />
            </ListItemButton>

            {/* Leave Organization */}
            <ListItemButton
              onClick={() => {
                setMobileDrawerOpen(false);
                setLeaveDialogOpen(true);
              }}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Leave Organization" />
            </ListItemButton>

            {/* Logout */}
            <ListItemButton
              onClick={() => {
                setMobileDrawerOpen(false);
                handleLogout();
              }}
              sx={{ borderRadius: 2, color: "error.main" }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ color: "error.main" }}
              />
            </ListItemButton>
          </Box>
        </Drawer>
      </Toolbar>
    </MuiAppBar>
  );
}
